import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { exportBoardBrief, fetchBoardBrief } from "../api/analyticsClient";

const EXPORTS = [
  { id: "pdf", label: "PDF" },
  { id: "docx", label: "DOCX" },
  { id: "pptx", label: "PPTX" },
  { id: "xlsx", label: "Excel" },
];

const actionButtonClass =
  "inline-flex h-9 items-center justify-center px-3.5 text-sm font-semibold leading-none rounded-lg transition-colors disabled:pointer-events-none";

function toBoardCompany(org) {
  if (!org || !org.name) return null;
  const company = {
    name: org.name,
    overall: org.overall,
    reportReady: org.reportReady,
    invited: org.invited,
    completed: org.completed,
    revenueLastYear: org.revenueLastYear,
    revenueLast12Months: org.revenueLast12Months,
    ebita: org.ebita,
    financial: org.financial,
    strategic: org.strategic,
    scalable: org.scalable,
    relentless: org.relentless,
    disciplined: org.disciplined,
    energized: org.energized,
  };
  if (Array.isArray(org.dimensions) && org.dimensions.length) {
    company.dimensions = org.dimensions.map((dimension) => ({
      label: dimension.label,
      score: dimension.score,
    }));
  }
  return company;
}

function renderInline(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

function BoardMarkdown({ markdown }) {
  const blocks = useMemo(() => {
    const lines = String(markdown || "").split("\n");
    const next = [];
    let list = null;
    const flush = () => {
      if (list) {
        next.push({ type: "ul", items: list });
        list = null;
      }
    };
    lines.forEach((line) => {
      if (line.startsWith("- ")) {
        list = list || [];
        list.push(line.slice(2));
        return;
      }
      flush();
      if (!line.trim()) return;
      if (line.startsWith("# ")) next.push({ type: "h1", text: line.slice(2) });
      else if (line.startsWith("## ")) next.push({ type: "h2", text: line.slice(3) });
      else if (line.trim() === "---") next.push({ type: "hr" });
      else next.push({ type: "p", text: line });
    });
    flush();
    return next;
  }, [markdown]);

  return (
    <div className="max-h-[min(520px,60vh)] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-950/40 px-4 py-3">
      {blocks.map((block, index) => {
        if (block.type === "h1") {
          return (
            <h3 key={index} className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              {block.text}
            </h3>
          );
        }
        if (block.type === "h2") {
          return (
            <h4
              key={index}
              className="mt-4 mb-2 text-sm font-semibold uppercase tracking-[0.08em] text-brand dark:text-cyan-300"
            >
              {block.text}
            </h4>
          );
        }
        if (block.type === "ul") {
          return (
            <ul key={index} className="mb-2 space-y-2 pl-4 list-disc text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "hr") {
          return <hr key={index} className="my-3 border-slate-200 dark:border-slate-700" />;
        }
        return (
          <p key={index} className="mb-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {renderInline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function BoardBriefCard({ context, companies, anomalies, dataReady }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const requestRef = useRef(0);
  const exportMenuRef = useRef(null);

  const payload = useMemo(() => {
    const mapped = (companies || []).slice(0, 150).map(toBoardCompany).filter(Boolean);
    return {
      ...(context || {}),
      companies: mapped.length ? mapped : context?.companies || [],
      anomalies: (anomalies || []).map((item) => ({
        type: item.type,
        severity: item.severity,
        title: item.title,
        detail: item.detail,
      })),
    };
  }, [anomalies, companies, context]);

  useEffect(() => {
    requestRef.current += 1;
    setBrief(null);
    setExportOpen(false);
  }, [payload]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onPointer = (event) => {
      if (!exportMenuRef.current?.contains(event.target)) setExportOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setExportOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [exportOpen]);

  const prepare = async () => {
    if (!dataReady || loading) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    try {
      const response = await fetchBoardBrief(payload);
      const next = response?.data;
      if (!next?.markdown) {
        throw new Error("Board brief was empty");
      }
      if (requestRef.current === requestId) setBrief(next);
    } catch (error) {
      if (requestRef.current === requestId) {
        toast.error(error?.message || "Could not prepare the board brief");
      }
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  const copyBrief = async () => {
    if (!brief?.markdown) return;
    try {
      await navigator.clipboard.writeText(brief.markdown);
      toast.success("Board brief copied");
    } catch {
      toast.error("Could not copy the brief");
    }
  };

  const exportBrief = async (format) => {
    if (!brief || exporting) return;
    setExporting(format);
    try {
      const blob = await exportBoardBrief(brief, format);
      downloadBlob(blob, `Board_Portfolio_Brief.${format}`);
      toast.success(`${format.toUpperCase()} ready`);
    } catch (error) {
      toast.error(error?.message || `Could not export ${format.toUpperCase()}`);
    } finally {
      setExporting("");
    }
  };

  const shareBrief = async () => {
    if (!brief?.markdown || exporting) return;
    const title = brief.title || "Board Portfolio Brief";
    setExporting("share");
    try {
      if (typeof navigator.share === "function") {
        let sharedFile = false;
        try {
          const blob = await exportBoardBrief(brief, "pdf");
          const file = new File([blob], "Board_Portfolio_Brief.pdf", { type: "application/pdf" });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ title, files: [file] });
            sharedFile = true;
          }
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
        if (!sharedFile) {
          await navigator.share({ title, text: brief.markdown });
        }
        return;
      }
      const body = brief.markdown.length <= 1800 ? brief.markdown : `${brief.markdown.slice(0, 1600)}…`;
      window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}`;
    } catch (error) {
      if (error?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(brief.markdown);
        toast.success("Sharing is not available here. The brief was copied instead.");
      } catch {
        toast.error("Could not share the brief");
      }
    } finally {
      setExporting("");
    }
  };

  return (
    <section
      className="mb-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 shadow-sm min-w-0"
      data-export-ignore
      aria-labelledby="board-brief-heading"
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h2 id="board-brief-heading" className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Board brief
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-3xl">
              Executive summary, milestones, and risks from the current portfolio view, written for a board pack.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={prepare}
              disabled={!dataReady || loading}
              className={`${actionButtonClass} text-white bg-brand hover:bg-brand-light shadow-sm disabled:opacity-50`}
            >
              {loading ? "Preparing…" : brief ? "Refresh" : "Prepare board brief"}
            </button>
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                onClick={() => setExportOpen((open) => !open)}
                disabled={!brief || loading}
                aria-haspopup="menu"
                aria-expanded={exportOpen}
                className={`${actionButtonClass} gap-1.5 text-brand dark:text-cyan-300 bg-brand/10 dark:bg-cyan-500/10 hover:bg-brand/20 dark:hover:bg-cyan-500/20 disabled:opacity-40`}
              >
                {exporting ? "Exporting…" : "Export"}
                <svg className={`w-4 h-4 transition-transform ${exportOpen ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {exportOpen && brief ? (
                <div
                  role="menu"
                  className="absolute right-0 z-30 mt-1.5 w-44 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-lg"
                >
                  <p className="px-3 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    Download
                  </p>
                  {EXPORTS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      disabled={Boolean(exporting)}
                      onClick={() => {
                        setExportOpen(false);
                        exportBrief(item.id);
                      }}
                      className="flex h-9 w-full items-center px-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-40"
                    >
                      {exporting === item.id ? "Exporting…" : item.label}
                    </button>
                  ))}
                  <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
                  <p className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    Share
                  </p>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={Boolean(exporting)}
                    onClick={() => {
                      setExportOpen(false);
                      shareBrief();
                    }}
                    className="flex h-9 w-full items-center px-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-40"
                  >
                    {exporting === "share" ? "Sharing…" : "Share"}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={Boolean(exporting)}
                    onClick={() => {
                      setExportOpen(false);
                      copyBrief();
                    }}
                    className="flex h-9 w-full items-center px-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-40"
                  >
                    Copy
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="mt-4">
          {brief?.markdown ? (
            <BoardMarkdown markdown={brief.markdown} />
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {dataReady
                ? "Prepare the brief when the dashboard reflects the portfolio you want in the board pack."
                : "The brief is available after portfolio data loads."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
